import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { FilePlus2, Files, FolderOpen, LucideIcon, Upload } from "lucide-react";
import UploadModal from "../components/modals/UploadModal";
import ApiService from "../services/ApiService";
import PageHeader from "../components/PageHeader";

interface ActionCard {
    icon: LucideIcon;
    title: string;
    description: string;
    buttonText: string;
    buttonVariant: string;
    onClick: () => void;
}

const QUICK_START_STEPS = ["template", "upload", "event", "create", "map", "generate"] as const;

interface HomeProps {
    apiService: ApiService;
    maxFileSizeBytes?: number;
}

const Home: React.FC<HomeProps> = (props) => {
    const { apiService, maxFileSizeBytes } = props;
    const { t } = useTranslation(["home", "common"]);
    const navigate = useNavigate();

    const [showUploadModal, setShowUploadModal] = React.useState(false);

    const handleUploadSuccess = () => {
        // Navigate to templates list to show the new template
        navigate("/templates");
    };

    const actionCards: ActionCard[] = [
        {
            icon: FilePlus2,
            title: t("actionCards.createDocuments.title"),
            description: t("actionCards.createDocuments.description"),
            buttonText: t("actionCards.createDocuments.buttonText"),
            buttonVariant: "primary",
            onClick: () => navigate("/documents/create"),
        },
        {
            icon: FolderOpen,
            title: t("actionCards.viewDocuments.title"),
            description: t("actionCards.viewDocuments.description"),
            buttonText: t("actionCards.viewDocuments.buttonText"),
            buttonVariant: "secondary",
            onClick: () => navigate("/documents"),
        },
        {
            icon: Upload,
            title: t("actionCards.uploadTemplate.title"),
            description: t("actionCards.uploadTemplate.description"),
            buttonText: t("actionCards.uploadTemplate.buttonText"),
            buttonVariant: "secondary",
            onClick: () => setShowUploadModal(true),
        },
        {
            icon: Files,
            title: t("actionCards.viewTemplates.title"),
            description: t("actionCards.viewTemplates.description"),
            buttonText: t("actionCards.viewTemplates.buttonText"),
            buttonVariant: "secondary",
            onClick: () => navigate("/templates"),
        },
    ];

    return (
        <Container fluid>
            <PageHeader title={t("common:base.title")} subtitle={t("hero.subtitle")} />

            <Row className="g-4">
                {actionCards.map((card, index) => (
                    <Col md={6} lg={3} key={index}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="d-flex flex-column">
                                <Card.Title className="mb-3 d-flex align-items-center gap-2">
                                    <card.icon size={20} />
                                    {card.title}
                                </Card.Title>
                                <Card.Text className="flex-grow-1 text-muted">{card.description}</Card.Text>
                                <Button variant={card.buttonVariant} onClick={card.onClick} className="mt-auto">
                                    {card.buttonText}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <div className="mt-5 pt-4 border-top">
                <Row>
                    <Col md={8}>
                        <h2 className="h5 mb-4">{t("quickStart.title")}</h2>
                        <ol className="list-unstyled quick-start-steps">
                            {QUICK_START_STEPS.map((step, index) => (
                                <li key={step} className="d-flex gap-3 mb-4">
                                    <span className="quick-start-number">{index + 1}</span>
                                    <div>
                                        <div className="fw-semibold">{t(`quickStart.steps.${step}.title`)}</div>
                                        <div className="small text-body-secondary">
                                            {step === "template" ? (
                                                <Trans
                                                    i18nKey="quickStart.steps.template.text"
                                                    ns="home"
                                                    components={[
                                                        <a
                                                            href="https://www.adobe.com/acrobat/hub/create-fillable-pdf-from-word-document.html"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Adobe Acrobat
                                                        </a>,
                                                        <a
                                                            href="https://www.sejda.com/de/pdf-forms"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Sejda
                                                        </a>,
                                                    ]}
                                                />
                                            ) : (
                                                t(`quickStart.steps.${step}.text`)
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Col>
                </Row>
            </div>

            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                apiService={apiService}
                onSuccess={handleUploadSuccess}
                maxFileSizeBytes={maxFileSizeBytes}
            />
        </Container>
    );
};

export default Home;
